import moment from "moment";
import React, { useEffect, useMemo, useState } from "react";
import { Button, FormControl, Modal, Spinner } from "react-bootstrap";
import {
  ChatText,
  Clock,
  PersonBadge,
  PlayFill,
  StopFill,
} from "react-bootstrap-icons";
import { useLocation, useParams } from "react-router-dom";
import { toast } from "react-toastify";
import { makeRESTApiRequests } from "../helpers/api";
import { ENDPOINTS } from "../helpers/constants";
import {
  DEFAULT_TIME_FORMATE,
  extractJobIdFromPathname,
  formatTime,
} from "../helpers/global";
import useTimeTracker from "../hooks/useTimeTracker";
import { getJobTitle } from "../Util/jobUtil";
import Loader from "./Loader";
import RippleEffect from "./RippleEffect";
import JobLIstModal from "./time-tracker/JobLIstModal";

const TimeTrackerModal = ({ show, onHide }) => {
  const {
    clockIn,
    clockOut,
    loading,
    activeTimeEntry,
    elapsedTime,
    activeJob,
  } = useTimeTracker();

  const [errors, setErrors] = useState(null);

  const [comment, setComment] = useState("");
  const [runningJob, setRunningJob] = useState(null);
  const [switchingJob, setSwitchingJob] = useState(null);
  const [loadingJob, setLoadingJob] = useState(false);
  const [jobId, setJobId] = useState(null);
  const [showJobList, setShowJobList] = useState(false);
  const [progress, setProgress] = useState(0);
  const [extendingTimer, setExtendingTimer] = useState(false);

  const { id: switchingJobId } = useParams();
  const { pathname } = useLocation();

  const isSwitchMode = useMemo(() => {
    return (
      (switchingJobId &&
        activeTimeEntry &&
        switchingJobId !== activeTimeEntry?.job?._id) ||
      (activeJob &&
        activeTimeEntry &&
        activeTimeEntry?.job?._id !== activeJob._id)
    );
  }, [switchingJobId, activeTimeEntry, activeJob]);

  const loadJobData = async ({ jobId, callBack }) => {
    if (!jobId) return;
    setLoadingJob(true);
    const { response, error } = await makeRESTApiRequests({
      endpoint: ENDPOINTS.SEARCH_JOBS,
      requestBody: {
        filter: { _id: jobId },
        query: "",
      },
    });

    if (error) {
      return toast.error(error);
    }

    if (response) {
      const jobData = response?.results[0];
      callBack && callBack(jobData);
    }
    setLoadingJob(false);
  };

  const handleExtendRunningTime = async () => {
    setExtendingTimer(true);
    const { response, error } = await makeRESTApiRequests({
      endpoint: ENDPOINTS.RESET_TIME,
      requestBody: {},
    });
    setExtendingTimer(false);

    if (error) {
      return toast.error(error);
    }
    if (response) {
      activeTimeEntry.clockOutExpiry = response?.clockOutExpiry;
      toast.success("Clock-out timer extended successfully.");
    }
  };

  const handleClockIn = () => {
    if (!jobId) {
      return setErrors("Job is Compulsory");
    }
    setErrors(null);

    let newJobId = isSwitchMode && switchingJobId ? switchingJobId : jobId;

    let currentJobId = activeTimeEntry?.job?._id;

    let clockInInfo = {
      job: newJobId,
      comment,
      clockOutJobId: currentJobId,
    };

    clockIn(clockInInfo);
    onHide();
  };

  const handleClockOut = () => {
    clockOut();
    onHide();
  };

  useEffect(() => {
    if (!activeTimeEntry) {
      setJobId(extractJobIdFromPathname(pathname));
      setComment("");
    } else {
      setJobId(activeTimeEntry?.job?._id || null);
      setComment(activeTimeEntry?.comment);
    }
    setErrors(null);
  }, [pathname, activeTimeEntry, show]);

  useEffect(() => {
    if (isSwitchMode) {
      loadJobData({
        jobId: switchingJobId,
        callBack: (jobs) => setSwitchingJob(jobs),
      });
    } else {
      setSwitchingJob(null);
    }
    if (activeJob && activeJob._id && isSwitchMode) {
      loadJobData({
        jobId: activeJob._id,
        callBack: (jobs) => setRunningJob(jobs),
      });
    }
  }, [isSwitchMode, switchingJobId, activeJob]);

  useEffect(() => {
    if (jobId) {
      loadJobData({ jobId, callBack: (jobs) => setRunningJob(jobs) });
    } else {
      setRunningJob(null);
    }
  }, [jobId]);

  useEffect(() => {
    if (activeTimeEntry) {
      onHide();
    }
  }, [activeTimeEntry]);

  const newSessions = async () => {
    try {
      await clockOut();
    } catch (error) {
      console.log(error.message);
    }
  };

  const handleSelectJob = (selectedJob) => {
    setRunningJob(selectedJob);
    setJobId(selectedJob._id);
    setShowJobList(false);
  };

  useEffect(() => {
    if (activeJob) {
      setRunningJob(activeJob);
      setJobId(activeJob._id);
    }
  }, [activeJob]);

  useEffect(() => {
    if (activeTimeEntry) {
      const timeCost =
        activeTimeEntry?.clockOutExpiry - activeTimeEntry?.startDateTime;
      const elapsedTime = Date.now() - activeTimeEntry?.startDateTime;
      const progressiveTime = (elapsedTime / timeCost) * 100;
      setProgress(progressiveTime);
    }
  }, [activeTimeEntry]);

  return (
    <>
      <Modal centered show={show} onHide={onHide} size="sm">
        <Modal.Header
          closeButton
          className="py-2 px-3 d-flex align-items-center"
        >
          <div className="w-100 d-flex align-items-center justify-content-between">
            <h6 className="mb-0 small-text">Time Logger</h6>
          </div>
        </Modal.Header>
        <Modal.Body className="">
          {loadingJob ? (
            <Loader />
          ) : (
            <div className="d-flex flex-column gap-3">
              {activeTimeEntry && (
                <>
                  <div className="border justify-content-center rounded border-primary d-flex align-items-center hover p-1 bg-primary text-white">
                    <RippleEffect size={16} />
                    <h6 className="clock-text large-text mb-0 px-3 ">
                      {formatTime(elapsedTime)}
                    </h6>
                  </div>
                  {isSwitchMode && (
                    <div
                      className="text-muted p-1 bg-light border-left border-warning"
                      style={{ borderLeftWidth: "4px" }}
                    >
                      <span className="ms-2 smallFont line">
                        You are already clocked in to{" "}
                        <strong>{getJobTitle(activeTimeEntry.job)}</strong>.{" "}
                        Switching will stop the previous timer and start a new
                        one.
                      </span>
                    </div>
                  )}
                  <div className="d-flex justify-content-center align-items-center gap-2">
                    <span className="ms-2 smallFont line">
                      You will be automatically clocked out at{" "}
                      <strong>
                        {moment(activeTimeEntry?.clockOutExpiry).format(
                          DEFAULT_TIME_FORMATE
                        )}
                      </strong>
                      .{" "}
                      <span
                        className="text-primary hover-light text-underline"
                        onClick={handleExtendRunningTime}
                      >
                        Click Here to Extend.
                      </span>
                      {extendingTimer && (
                        <Spinner animation="border" size="sm" />
                      )}
                    </span>
                  </div>

                  <hr className="w-100 my-1" />
                </>
              )}
              <div className="">
                <h6 className="mb-1 small-text fw-bold d-flex align-items-center gap-2">
                  <Clock /> {activeTimeEntry ? "Started On" : "Time Now"}
                </h6>
                <h6 className="mb-0 small border rounded p-1">
                  {" "}
                  {moment(activeTimeEntry?.startDateTime || new Date()).format(
                    "hh:mm a"
                  )}
                </h6>
              </div>
              <div className="">
                <h6 className="mb-1 small-text fw-bold d-flex align-items-center gap-2">
                  <PersonBadge /> Job
                </h6>

                {isSwitchMode ? (
                  <div className="p-1 smallFont w-100 bg-white border rounded">
                    {/* {getJobTitle(switchingJob)} */}
                    {switchingJob
                      ? getJobTitle(switchingJob)
                      : activeJob
                      ? getJobTitle(activeJob)
                      : "No Job Selected"}
                  </div>
                ) : (
                  <div
                    className="p-1 smallFont w-100 border rounded cursor-pointer hover-light"
                    onClick={() => {
                      setShowJobList(true);
                    }}
                  >
                    {runningJob ? getJobTitle(runningJob) : "Select A Job"}
                  </div>
                )}
              </div>
              {errors && (
                <h6 className="text-danger mb-0 smallFont">{errors}</h6>
              )}
              <div className="">
                <h6 className="mb-1 small-text fw-bold d-flex align-items-center gap-2">
                  <ChatText />
                  {isSwitchMode ? "Comment" : "Working On"}
                </h6>

                {!isSwitchMode && activeTimeEntry ? (
                  <div className="p-1 smallFont w-100 bg-white border rounded">
                    {activeTimeEntry?.comment || "----"}
                  </div>
                ) : !activeTimeEntry ? (
                  <FormControl
                    as={"textarea"}
                    cols={5}
                    style={{
                      fontSize: 12,
                    }}
                    className="p-1 smallFont"
                    placeholder="Working on..."
                    value={comment}
                    onChange={(e) => setComment(e.target.value)}
                  />
                ) : (
                  <FormControl
                    as={"textarea"}
                    cols={5}
                    style={{
                      fontSize: 12,
                    }}
                    className="p-1 smallFont"
                    placeholder="Working on..."
                    value={comment}
                    onChange={(e) => setComment(e.target.value)}
                  />
                )}
              </div>{" "}
              {isSwitchMode && (
                <Button
                  variant={"success"}
                  className="d-flex gap-2 justify-content-center align-items-center"
                  size="sm"
                  onClick={handleClockIn}
                >
                  <h6 className="mb-0 small-text"> Switch To This Job</h6>
                </Button>
              )}
              <Button
                disabled={loading}
                size="sm"
                variant={activeTimeEntry ? "danger" : "success"}
                className="d-flex gap-2 justify-content-center align-items-center"
                onClick={() =>
                  activeTimeEntry ? handleClockOut() : handleClockIn()
                }
              >
                {activeTimeEntry ? (
                  <StopFill size={20} />
                ) : (
                  <PlayFill size={20} />
                )}{" "}
                <h6 className="mb-0 small-text">
                  {" "}
                  {activeTimeEntry ? "Stop" : "Start Now"}{" "}
                </h6>
              </Button>
              {activeTimeEntry && !isSwitchMode && (
                <Button
                  variant={"success"}
                  className="d-flex gap-2 justify-content-center align-items-center"
                  size="sm"
                  onClick={() => newSessions()}
                >
                  <PlayFill size={20} />
                  Start New Session
                </Button>
              )}
            </div>
          )}
        </Modal.Body>
      </Modal>

      {JobLIstModal && (
        <JobLIstModal
          show={showJobList}
          onHide={() => setShowJobList(false)}
          onSelectJob={handleSelectJob}
        />
      )}
    </>
  );
};

export default TimeTrackerModal;
